import { Injectable } from '@angular/core';
import moment from 'moment';
import { PatientService } from 'src/app/services/patient/patient.service';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import {AtcdPanelSection, AtcdPanelItem,AtcdPanelCategoryKey} from 'src/app/fragments/atcd-side-panel/atcd-side-panel.model';

@Injectable({
  providedIn: 'root'
})
export class AtcdPanelService {

  private cache = new Map<string, AtcdPanelSection[]>();

  constructor(private patientService: PatientService) {}

  public async getAtcd(patientId: string): Promise<AtcdPanelSection[]> {
    if (!patientId) {
      return [];
    }

    if (this.cache.has(patientId)) {
      return this.cache.get(patientId)!;
    }

    const data = await this.loadFromApi(patientId);
    this.cache.set(patientId, data);

    return data;
  }

  public clear(patientId: string): void {
    if(!patientId)return;
    this.cache.delete(patientId);
  }

  public clearAll(): void {
    this.cache.clear();
  }

  private async loadFromApi(patientId: string): Promise<AtcdPanelSection[]> {
    const filtresAntecedentsPerso: Filtres = {
      currentPage: 1,
      perpage: 10,
      sortBy: 'dateAntecedent',
      sortOrder: SortEnum.DESC
    };

    const filtresAntecedentsFamilial: Filtres = {
      currentPage: 1,
      perpage: 10,
      sortBy: 'creationDate',
      sortOrder: SortEnum.DESC
    };

    const filtresAllergies: Filtres = {
      currentPage: 1,
      perpage: 10,
      sortBy: 'dateAllergie',
      sortOrder: SortEnum.DESC
    };

    const filtresPathologies: Filtres = {
      currentPage: 1,
      perpage: 10,
      sortBy: 'datePathologie',
      sortOrder: SortEnum.DESC
    };

    const filtresHisto: Filtres = {
      currentPage: 1,
      perpage: 10,
      sortBy: 'dateHistorique',
      sortOrder: SortEnum.DESC
    };

    const filtresAld: Filtres = {
      currentPage: 1,
      perpage: 10,
      sortBy: 'dateDemande',
      sortOrder: SortEnum.DESC
    };

    const [persoNC,persoAllNC,persoObs,persoMed,persoChir,
      persoProf,familial,allergies,pathologies,histo,ald] = await Promise.all([
      this.patientService.findOnePatientAntecedentsPerso(patientId,filtresAntecedentsPerso,'f3d42e80-5ce6-4dee-9034-8f56e6790dd2'),
      this.patientService.findOnePatientAntecedentsPerso(patientId,filtresAntecedentsPerso,'b4441376-371f-4c16-a18e-dcd69788a4c1'),
      this.patientService.findOnePatientAntecedentsPerso(patientId,filtresAntecedentsPerso,'520d9063-68ce-4156-937d-301dd1647926'),
      this.patientService.findOnePatientAntecedentsPerso(patientId,filtresAntecedentsPerso,'99f5811f-e783-4ee0-88f0-848cb379cadf'),
      this.patientService.findOnePatientAntecedentsPerso(patientId,filtresAntecedentsPerso,'18652137-1329-4359-99de-1383f99852ad'),
      this.patientService.findOnePatientAntecedentsPerso(patientId,filtresAntecedentsPerso,'2a5e7ba1-2d7a-41ec-8494-1e8634bed317'),
      this.patientService.findOnePatientAntecedentsFamilial(patientId,filtresAntecedentsFamilial),
      this.patientService.findOnePatientAllergies(patientId,filtresAllergies),
      this.patientService.findOnePatientPathologies(patientId,filtresPathologies),
      this.patientService.findOnePatientHistoMedicoSocial(patientId,filtresHisto),
      this.patientService.findOnePatientALD(patientId,filtresAld)
    ]);

    const sections: AtcdPanelSection[] = [];

    if (persoMed?.objects?.length) {
      sections.push(this.createSection(
        'medical',
        'ATCD médicaux',
        persoMed.objects.map((a: any) =>
          this.buildDatedItem(
            a.id,
            this.getAntecedentLabel(a),
            a.dateAntecedent,
            a.commentaire,
            a.code
          )
        ),
        true,
        false
      ));
    }

    if (persoChir?.objects?.length) {
      sections.push(this.createSection(
        'chirurgical',
        'ATCD chirurgicaux',
        persoChir.objects.map((a: any) =>
          this.buildDatedItem(
            a.id,
            this.getChirurgicalLabel(a),
            a.dateAntecedent,
            a.commentaire,
            a.code
          )
        ),
        true,
        false
      ));
    }

    if (persoObs?.objects?.length) {
      sections.push(this.createSection(
        'obstetrical',
        'ATCD obstétricaux',
        persoObs.objects.map((a: any) =>
          this.buildDatedItem(
            a.id,
            this.getAntecedentLabel(a),
            a.dateAntecedent,
            a.commentaire,
            a.code
          )
        ),
        true,
        true,
        true
      ));
    }

    if (persoNC?.objects?.length) {
      sections.push(this.createSection(
        'uncoded',
        'ATCD non codés',
        persoNC.objects.map((a: any) =>
          this.buildDatedItem(
            a.id,
            this.getAntecedentLabel(a),
            a.dateAntecedent,
            a.commentaire,
            a.code
          )
        ),
        true,
        true,
        true
      ));
    }

    if (persoAllNC?.objects?.length) {
      sections.push(this.createSection(
        'allergy-uncoded',
        'ATCD allergie non codée',
        persoAllNC.objects.map((a: any) =>
          this.buildDatedItem(
            a.id,
            this.getAntecedentLabel(a),
            a.dateAntecedent,
            a.commentaire,
            a.code
          )
        ),
        true,
        true,
        true
      ));
    }

    if (persoProf?.objects?.length) {
      sections.push(this.createSection(
        'professional',
        'ATCD professionnels',
        persoProf.objects.map((a: any) => ({
          id: a.id,
          label: a.commentaire || 'Inconnu',
          comment: '',
          code: a.code,
          dateLabel: this.formatDate(a.dateAntecedent)
        })),
        true,
        false
      ));
    }

    if (familial?.objects?.length) {
      sections.push(this.createSection(
        'family',
        'ATCD familiaux',
        familial.objects.map((a: any) => ({
          id: a.id,
          label: `${this.safeText(a.idParente?.libelle || a.idParente || 'Parent inconnu')} : ${this.safeText(a.antecedent?.libelle || a.antecedent || 'Inconnu')}`,
          comment: a.commentaire || '',
          code: a.code,
          dateLabel: ''
        })),
        true,
        false
      ));
    }

    if (histo?.objects?.length) {
      sections.push(this.createSection(
        'medico-social-history',
        'Historique médico-social',
        histo.objects.map((a: any) => ({
          id: a.id,
          label: this.safeText(a.historique?.libelle || a.historique || a.histoNoCodedField || 'Inconnu'),
          comment: this.buildHistoComment(a),
          dateLabel: this.extractYearLabel(a.dateHistorique)
        })),
        true,
        false
      ));
    }

    if (allergies?.objects?.length) {
      sections.push(this.createSection(
        'allergy',
        'Allergies',
        allergies.objects.map((a: any) => ({
          id: a.id,
          label: this.safeText(a.allergene?.displayName || 'Allergie inconnue'),
          comment: a.allergieDescriptionNoCoded || '',
          dateLabel: this.formatDate(a.dateAllergie || a.date)
        })),
        true,
        false
      ));
    }

    if (pathologies?.objects?.length) {
      sections.push(this.createSection(
        'pathology',
        'Pathologies',
        pathologies.objects.map((a: any) => ({
          id: a.id,
          label: this.safeText(a.pathologie?.libelle || a.pathologie || 'Pathologie inconnue'),
          comment: a.commentaire || '',
          dateLabel: this.formatDate(a.datePathologie || a.date)
        })),
        true,
        false
      ));
    }

    const aldFiltered = (ald?.objects || []).filter((a: any) =>
      !a.dateFinETM || moment(a.dateFinETM).isAfter(moment(), 'day')
    );

    if (aldFiltered.length) {
      sections.push(this.createSection(
        'ald',
        'ALD',
        aldFiltered.map((a: any) => ({
          id: a.id,
          label: this.safeText(a.nomPathologie || 'ALD'),
          comment: a.commentaire || '',
          dateLabel: this.buildAldDateLabel(a.dateDemande, a.dateFinETM)
        })),
        true,
        false
      ));
    }

    return sections;
  }

  private createSection(
    key: AtcdPanelCategoryKey,
    label: string,
    items: AtcdPanelItem[],
    enabled = true,
    expanded = false,
    droppable = false
  ): AtcdPanelSection {
    return {
      key,
      label,
      enabled,
      expanded,
      items,
      droppable
    };
  }

  private formatDate(date?: string): string {
    return date ? moment(date).format('DD/MM/YYYY') : '';
  }

  private extractYearLabel(date?: string): string {
    return date ? moment(date).format('YYYY') : '';
  }

  private buildDatedItem(
    id: string,
    label: string,
    date?: string,
    comment?: string,
    code?: string
  ): AtcdPanelItem {
    return {
      id,
      label: this.safeText(label || 'Inconnu'),
      comment: comment || '',
      code,
      dateLabel: this.formatDate(date)
    };
  }

  private buildAldDateLabel(dateDemande?: string, dateFinETM?: string): string {
    const start = this.formatDate(dateDemande);
    const end = this.formatDate(dateFinETM);

    if (start && end) {
      return `${start} → ${end}`;
    }

    if (start) {
      return start;
    }

    if (end) {
      return `Fin ${end}`;
    }

    return '';
  }

  private getAntecedentLabel(atcd: any): string {
    return this.safeText(
      atcd?.antecedent?.libelle ||
      atcd?.commentaire ||
      'Inconnu'
    );
  }

  private getChirurgicalLabel(atcd: any): string {
    const libelle = atcd?.antecedent?.libelle;
    if (typeof libelle === 'string' && libelle.includes(' — ')) {
      return this.safeText(libelle.split(' — ')[1]?.trim() || libelle);
    }

    return this.safeText(libelle || atcd?.commentaire || 'Inconnu');
  }

  private buildHistoComment(atcd: any): string {
    const parts: string[] = [];

    if (atcd?.commentaire) {
      parts.push(this.safeText(atcd.commentaire));
    }

    return parts.join(' - ');
  }

  private safeText(value: any): string {
    if (typeof value === 'string') {
      return value;
    }

    if (value === null || value === undefined) {
      return '';
    }

    return String(value);
  }
}
